
import { CreditGrid } from "../CreditGrid";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const UnpaidCreditsTab = () => {
  const [paymentAmounts, setPaymentAmounts] = useState<Record<string, string>>({});

  const { data: transactions = [], isLoading, refetch } = useQuery({
    queryKey: ["unpaid-credits"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      console.log("Récupération des crédits impayés...");

      // Récupérer toutes les transactions de vente avec un montant restant à payer
      const { data, error } = await supabase
        .from("poultry_transactions")
        .select(`
          id,
          created_at,
          transaction_date,
          total_amount,
          paid_amount,
          remaining_amount,
          payment_due_date,
          customer_name,
          customer_contact,
          notes,
          stock_items!poultry_transactions_stock_item_id_fkey (
            id,
            quantity,
            data
          )
        `)
        .eq('created_by', user.id)
        .eq('transaction_type', 'sale')
        .gt('remaining_amount', 0)
        .order('transaction_date', { ascending: false });

      if (error) {
        console.error("Erreur lors de la récupération des crédits impayés:", error);
        return [];
      }

      // Regrouper par client et date de transaction
      const groupedTransactions = data.reduce((acc: any, transaction) => {
        // Utiliser les notes comme clé car elles contiennent les informations de la vente
        const key = transaction.notes;
        
        if (!acc[key]) {
          // Première transaction pour ce groupe
          acc[key] = {
            id: transaction.id,
            created_at: transaction.created_at,
            transaction_date: transaction.transaction_date,
            customer_name: transaction.customer_name || "Client non spécifié",
            customer_contact: transaction.customer_contact,
            payment_due_date: transaction.payment_due_date,
            total_amount: Number(transaction.total_amount),
            paid_amount: Number(transaction.paid_amount || 0),
            remaining_amount: Number(transaction.remaining_amount),
            notes: transaction.notes,
            stock_items: [transaction.stock_items]
          };
        } else {
          // Ajouter les montants à ceux existants
          acc[key].total_amount += Number(transaction.total_amount);
          acc[key].paid_amount += Number(transaction.paid_amount || 0);
          acc[key].remaining_amount += Number(transaction.remaining_amount);
          acc[key].stock_items.push(transaction.stock_items);
        }
        
        return acc;
      }, {});

      const groupedArray = Object.values(groupedTransactions);
      console.log("Transactions groupées:", groupedArray);
      return groupedArray;
    }
  });

  const handlePaymentAmountChange = (id: string, value: string) => {
    setPaymentAmounts(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handlePayment = async (id: string, remainingAmount: number) => {
    const paymentAmount = parseFloat(paymentAmounts[id] || "0");
    
    if (paymentAmount <= 0) {
      toast.error("Le montant du paiement doit être supérieur à 0");
      return;
    }
    
    if (paymentAmount > remainingAmount) {
      toast.error("Le montant du paiement ne peut pas être supérieur au montant restant");
      return;
    }

    try {
      const { data: transaction, error: fetchError } = await supabase
        .from("poultry_transactions")
        .select("paid_amount, remaining_amount, stock_item_id")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      const newPaidAmount = (transaction.paid_amount || 0) + paymentAmount;
      const newRemainingAmount = (transaction.remaining_amount || 0) - paymentAmount;

      // 1. Mettre à jour la transaction de crédit originale
      const { error: updateError } = await supabase
        .from("poultry_transactions")
        .update({
          paid_amount: newPaidAmount,
          remaining_amount: newRemainingAmount,
          is_fully_paid: newRemainingAmount === 0
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // 2. Créer une nouvelle transaction pour le paiement du crédit
      const { error: creditPaymentError } = await supabase
        .from("poultry_transactions")
        .insert({
          stock_item_id: transaction.stock_item_id,
          transaction_type: "credit_payment",
          quantity: 1,
          unit_price: paymentAmount,
          total_amount: paymentAmount,
          transaction_date: new Date().toISOString(),
          notes: `Paiement de crédit - Référence: ${id}`,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          payment_method: 'cash',
          is_deducted_from_revenue: false
        });

      if (creditPaymentError) throw creditPaymentError;

      // Réinitialiser le montant du paiement
      setPaymentAmounts(prev => ({
        ...prev,
        [id]: ""
      }));

      // Rafraîchir les données
      refetch();
      
      toast.success("Paiement effectué avec succès");
    } catch (error) {
      console.error("Erreur lors du paiement:", error);
      toast.error("Erreur lors du paiement");
    }
  };

  return (
    <div className="space-y-4">
      <CreditGrid
        transactions={transactions}
        isLoading={isLoading}
        paymentAmounts={paymentAmounts}
        onPaymentAmountChange={handlePaymentAmountChange}
        onPayment={handlePayment}
      />
    </div>
  );
};
